import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as CopyIcon } from "../../assets/icons/copy.svg";
import { ReactComponent as PagesIcon } from "../../assets/icons/pages.svg";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as ShareIcon } from "../../assets/icons/share.svg";
import { ReactComponent as ShareAltIcon } from "../../assets/icons/share_alt.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import useReaderStore from "../../context/readerStore";
import useActionGuard from "../../hooks/useActionGuard";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { removeHighlightById } from "../../services/highlightService";
import { removeNoteById } from "../../services/noteService";
import { showComingSoonNotification } from "../../services/notificationService";
import { deleteUnderline } from "../../services/underlineService";
import "./Notes.css";

const TABS = ["all", "highlight", "underline", "note"];

const NotebookItemCard = ({
  item,
  t,
  onJump,
  onDelete,
  onCopy,
  isDeleting,
}) => {
  const pageLabel = t("notes.pageTitle", { page: item.page });

  const handleCardActivate = () => {
    if (typeof onJump === "function") onJump(item);
  };

  return (
    <article
      className="notebook-card"
      data-type={item.type}
      role={onJump ? "button" : undefined}
      tabIndex={onJump ? 0 : undefined}
      onClick={onJump ? handleCardActivate : undefined}
      onKeyDown={
        onJump
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleCardActivate();
              }
            }
          : undefined
      }
    >
      <div className="notebook-card-header">
        <div className="notebook-card-page">
          <PagesIcon className="notebook-card-page-icon" aria-hidden="true" />
          <span>{pageLabel}</span>
        </div>
        <div className="notebook-card-actions">
          <button
            type="button"
            className="notebook-card-action"
            aria-label={t("notes.copyItem")}
            title={t("notes.copyItem")}
            onClick={(event) => {
              event.stopPropagation();
              if (typeof onCopy === "function") onCopy(item);
            }}
          >
            <CopyIcon aria-hidden="true" />
          </button>
          <button
            type="button"
            className="notebook-card-action"
            aria-label={t("notes.shareItem")}
            title={t("notes.shareItem")}
            onClick={(event) => {
              event.stopPropagation();
              showComingSoonNotification("notes_share_item");
            }}
          >
            <ShareAltIcon aria-hidden="true" />
          </button>
          <button
            type="button"
            className="notebook-card-action notebook-card-action--danger"
            aria-label={t("notes.remove")}
            title={t("notes.remove")}
            disabled={isDeleting}
            onClick={(event) => {
              event.stopPropagation();
              if (typeof onDelete === "function") onDelete(item);
            }}
          >
            <TrashIcon className="preserve-original-fill" aria-hidden="true" />
          </button>
        </div>
      </div>

      {item.type === "highlight" && (
        <div
          className="notebook-card-highlight"
          style={{ backgroundColor: item.color || "#F6E58D" }}
        >
          <p>{item.text}</p>
        </div>
      )}

      {item.type === "underline" && (
        <p className="notebook-card-underline">{item.text}</p>
      )}

      {item.type === "note" && (
        <div className="notebook-card-note">
          {item.text ? (
            <p className="notebook-card-note-excerpt">{item.text}</p>
          ) : null}
          {item.note ? (
            <p className="notebook-card-note-body">{item.note}</p>
          ) : null}
        </div>
      )}
    </article>
  );
};

const Notes = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    toggleNotesSidebar,
    isNotesSidebarOpen,
    setCurrentPage,
    documentType,
  } = useReaderStore();
  const notesFromStore = useReaderStore((s) => s.annotations.notes.data);
  const highlightsFromStore = useReaderStore(
    (s) => s.annotations.highlights.data,
  );
  const underlinesFromStore = useReaderStore(
    (s) => s.annotations.underlines.data,
  );
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const notebookItems = useMemo(() => {
    const notes = Array.isArray(notesFromStore) ? notesFromStore : [];
    const highlights = Array.isArray(highlightsFromStore)
      ? highlightsFromStore
      : [];
    const underlines = Array.isArray(underlinesFromStore)
      ? underlinesFromStore
      : [];

    const mappedNotes = notes.map((note) => ({
      id: `note-${note.id}`,
      sourceId: note.id,
      type: "note",
      page: note.page_number || 1,
      text: note.selected_text || note.quote || "",
      note: note.content || "",
      timestamp: note.timestamp,
      raw: note,
    }));

    const mappedHighlights = highlights.map((hl) => ({
      id: `highlight-${hl.id}`,
      sourceId: hl.id,
      type: "highlight",
      page: hl.page_number || 1,
      text: hl.content || hl.selected_text || hl.quote || "",
      color: hl.color || hl.highlight_color || "#F6E58D",
      timestamp: hl.timestamp || hl.created_at || null,
      raw: hl,
    }));

    const mappedUnderlines = underlines.map((ul) => ({
      id: `underline-${ul.id}`,
      sourceId: ul.id,
      type: "underline",
      page: ul.page_number || 1,
      text: ul.content || ul.selected_text || ul.quote || "",
      timestamp: ul.timestamp || ul.created_at || null,
      raw: ul,
    }));

    return [...mappedHighlights, ...mappedUnderlines, ...mappedNotes].sort(
      (a, b) =>
        new Date(b.timestamp || 0).getTime() -
        new Date(a.timestamp || 0).getTime(),
    );
  }, [notesFromStore, highlightsFromStore, underlinesFromStore]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return notebookItems.filter((item) => {
      if (activeTab !== "all" && item.type !== activeTab) return false;
      if (!query) return true;
      return (
        String(item.text || "")
          .toLowerCase()
          .includes(query) ||
        String(item.note || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [notebookItems, activeTab, searchQuery]);

  const handleNoteJump = (item) => {
    if (!item || item.type !== "note" || !item.raw) return;
    const targetPage = Number(item.page || 1);

    if (documentType === "pdf" && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("reader:go-to-page", {
          detail: { page: targetPage },
        }),
      );
    } else if (documentType === "epub") {
      // EPUB viewer navigates via note CFI (or fixed synthetic seek) in
      // reader:open-note — do not setCurrentPage here (that raced the seek).
    } else {
      setCurrentPage(targetPage);
    }

    try {
      window.dispatchEvent(
        new CustomEvent("reader:open-note", { detail: { note: item.raw } }),
      );
    } catch (err) {
      console.error("Could not dispatch reader:open-note event:", err);
    }

    if (isMobile) toggleNotesSidebar();
  };

  const handleAnnotationJump = (item) => {
    if (!item) return;
    const targetPage = Number(item.page || 1);
    const coordinates = item.raw?.coordinates;
    const cfiRange =
      coordinates?.cfiRange ??
      coordinates?.cfi_range ??
      (Array.isArray(coordinates)
        ? (coordinates[0]?.cfiRange ?? coordinates[0]?.cfi_range)
        : null) ??
      null;
    const chapterNumber =
      item.raw?.chapter_number != null
        ? Number(item.raw.chapter_number)
        : null;

    if (documentType === "pdf" && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("reader:go-to-page", {
          detail: { page: targetPage },
        }),
      );
    } else if (documentType === "epub" && cfiRange) {
      // EPUB: navigate by CFI so synthetic-page seek cannot land on the wrong column.
      // The viewer handles same-chapter display(cfi) and cross-chapter load+display.
      try {
        window.dispatchEvent(
          new CustomEvent("reader:focus-annotation", {
            detail: {
              type: item.type,
              id: item.sourceId,
              cfiRange,
              chapter_number: chapterNumber,
              page: targetPage,
            },
          }),
        );
      } catch (err) {
        // ignore
      }
      if (isMobile) toggleNotesSidebar();
      return;
    } else {
      setCurrentPage(targetPage);
    }

    try {
      window.dispatchEvent(
        new CustomEvent("reader:focus-annotation", {
          detail: {
            type: item.type,
            id: item.sourceId,
            cfiRange,
            chapter_number: chapterNumber,
            page: targetPage,
          },
        }),
      );
    } catch (err) {
      // ignore
    }

    if (isMobile) toggleNotesSidebar();
  };

  const executeNoteDelete = (item) => {
    if (!item?.sourceId) return;
    removeNoteById(item.sourceId);
  };

  const { run: handleNoteDelete, isPending: isNoteDeletePending } =
    useActionGuard(executeNoteDelete, {
      keyBy: (item) => item?.sourceId,
    });

  const executeHighlightDelete = (item) => {
    if (!item?.sourceId) return;
    removeHighlightById(item.sourceId);
  };

  const { run: handleHighlightDelete, isPending: isHighlightDeletePending } =
    useActionGuard(executeHighlightDelete, {
      keyBy: (item) => item?.sourceId,
    });

  const executeUnderlineDelete = (item) => {
    if (!item?.sourceId) return;
    deleteUnderline(item.sourceId);
  };

  const { run: handleUnderlineDelete, isPending: isUnderlineDeletePending } =
    useActionGuard(executeUnderlineDelete, {
      keyBy: (item) => item?.sourceId,
    });

  const handleCopy = async (item) => {
    try {
      const toCopy = item?.text || item?.note || "";
      if (toCopy && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(toCopy);
      }
    } catch (err) {
      // ignore
    }
  };

  return (
    <div
      className={`notes ${isNotesSidebarOpen ? "open" : ""}`}
      role="complementary"
      aria-label={t("notes.title")}
    >
      <div className="notes-header">
        <div className="notes-header-left">
          <button
            type="button"
            className="notes-icon-button"
            onClick={toggleNotesSidebar}
            aria-label={t("notes.close")}
            title={t("notes.close")}
          >
            <CloseIcon width={20} height={20} aria-hidden="true" />
          </button>
          <h3 tabIndex="-1">{t("notes.title")}</h3>
        </div>
        <div className="notes-header-right">
          <button
            type="button"
            className="notes-icon-button is-coming-soon"
            aria-label={t("notes.share")}
            title={t("notes.share")}
            onClick={() => showComingSoonNotification("notes_share")}
          >
            <ShareIcon
              className="notes-share-icon"
              width={20}
              height={20}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <div className="notes-search">
        <div className="notes-search-field">
          <input
            type="text"
            className="notes-search-input"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t("notes.searchPlaceholder")}
            aria-label={t("notes.searchPlaceholder")}
          />
          {searchQuery ? (
            <button
              type="button"
              className="notes-search-action"
              onClick={() => setSearchQuery("")}
              aria-label={t("notes.clearSearch")}
              title={t("notes.clearSearch")}
            >
              <CloseIcon width={16} height={16} aria-hidden="true" />
            </button>
          ) : (
            <SearchIcon className="notes-search-icon" aria-hidden="true" />
          )}
        </div>
      </div>

      <div
        className="notes-tabs"
        role="tablist"
        aria-label={t("notes.tabsLabel")}
      >
        {TABS.map((tabId) => {
          const isActive = activeTab === tabId;
          return (
            <button
              key={tabId}
              type="button"
              role="tab"
              id={`notes-tab-${tabId}`}
              aria-selected={isActive}
              aria-controls="notes-panel"
              className={`notes-tab${isActive ? " is-active" : ""}`}
              onClick={() => setActiveTab(tabId)}
            >
              {t(`notes.tabs.${tabId}`)}
            </button>
          );
        })}
      </div>

      <div className="notes-results-bar">
        <span>{t("notes.resultsFound", { count: filteredItems.length })}</span>
      </div>

      <div
        className="notes-content"
        id="notes-panel"
        role="tabpanel"
        aria-labelledby={`notes-tab-${activeTab}`}
      >
        {filteredItems.length > 0 ? (
          <div className="notebook-list">
            {filteredItems.map((item) => (
              <NotebookItemCard
                key={item.id}
                item={item}
                t={t}
                onJump={
                  item.type === "note"
                    ? handleNoteJump
                    : item.type === "highlight" || item.type === "underline"
                      ? handleAnnotationJump
                      : undefined
                }
                onDelete={
                  item.type === "note"
                    ? handleNoteDelete
                    : item.type === "highlight"
                      ? handleHighlightDelete
                      : item.type === "underline"
                        ? handleUnderlineDelete
                        : undefined
                }
                onCopy={handleCopy}
                isDeleting={
                  item.type === "note"
                    ? isNoteDeletePending(item.sourceId)
                    : item.type === "highlight"
                      ? isHighlightDeletePending(item.sourceId)
                      : item.type === "underline"
                        ? isUnderlineDeletePending(item.sourceId)
                        : false
                }
              />
            ))}
          </div>
        ) : (
          <div className="notes-empty">
            <p>{t("notes.noItems")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notes;
